// @flow
import React, { useCallback, useMemo } from "react";
import styles from "./FolderTree.module.css";
import {
    getLocalizedText,
    isSpecialFolderItemUri,
    getClassNames,
} from "data/utils";
import { openItemActionPanel } from "components/ItemActionPanel";
import { isHandheld } from "data/bowser";
import RowContentWithStickyToggle from "./RowContent";
import Pagination from "./Pagination";

const Row = ({
    index,
    isScrolling,
    isVisible,
    style,
    hasNext,
    hasPrev,
    tree,
    onLoadMore,
    onLoadPrev,
    activeTreeNodeId,
    onDoubleClick,
    onClick,
    onExpand,
    filter,
    viewName,
    status,
}: Props) => {
    const calcIndex = !!hasPrev ? index - 1 : index;
    const treeItem = tree[calcIndex];

    /* istanbul ignore next */
    const localizedWebDavName = useMemo(
        () =>
            !isSpecialFolderItemUri(treeItem?.itemUri)
                ? treeItem?.webDavName
                : getLocalizedText(treeItem?.webDavName),
        [treeItem]
    );

    const handleDoubleClick = useCallback(
        (e) => onDoubleClick(treeItem, e),
        [treeItem, onDoubleClick]
    );
    const handleClick = useCallback(
        (e) => onClick(treeItem, e),
        [treeItem, onClick]
    );

    const handleContextClick = useCallback(
        (e) => {
            e.preventDefault();
            openItemActionPanel({
                itemUri: treeItem?.itemUri,
                title: localizedWebDavName,
                contextProps: { itemUri: treeItem?.itemUri, viewName },
                targetView: viewName,
            });
            onClick(treeItem, e);
        },
        [treeItem, localizedWebDavName, onClick, viewName]
    );

    if (hasPrev && index === 0) {
        return (
            <Pagination
                node={null}
                className={getClassNames(
                    styles.listGroupItem,
                    styles.pagination,
                    styles.prev
                )}
                key="TREE$PREV"
                onPaginate={onLoadPrev}
                style={style}
                icon="backward-step"
                label={getLocalizedText("$folder:treefilter.loadmore")}
                status={status}
            />
        );
    }
    if (hasNext && calcIndex === tree.length) {
        return (
            <Pagination
                node={null}
                className={getClassNames(
                    styles.listGroupItem,
                    styles.pagination,
                    styles.next
                )}
                key="TREE$MORE"
                onPaginate={onLoadMore}
                style={style}
                icon="forward-step"
                label={getLocalizedText("$folder:treefilter.loadmore")}
                status={status}
            />
        );
    }

    return (
        <div
            className={getClassNames(
                styles.listGroupItem,
                activeTreeNodeId === treeItem.id
                    ? styles.listGroupItemActive
                    : ""
            )}
            key={treeItem.id}
            style={style}
            title={treeItem.bc || ""}
            onDoubleClick={handleDoubleClick}
            onClick={handleClick}
            onContextMenu={handleContextClick}
            data-test={`folderTreeRow-${localizedWebDavName.replace(" ", "")}`}
        >
            <RowContentWithStickyToggle
                index={index}
                isScrolling={isScrolling}
                isVisible={isVisible}
                tree={tree}
                onLoadMore={onLoadMore}
                activeTreeNodeId={activeTreeNodeId}
                onDoubleClick={onDoubleClick}
                onExpand={onExpand}
                filter={filter}
                localizedWebDavName={localizedWebDavName}
                treeItem={treeItem}
                onMoreClick={handleContextClick}
                isActive={activeTreeNodeId === treeItem.id}
                isHovered={!isHandheld()}
            />
        </div>
    );
};

export default Row;
